import imageUrlBuilder from '@sanity/image-url';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';

const projectId = process.env.REACT_APP_SANITY_PROJECT_ID || '';
const dataset = process.env.REACT_APP_SANITY_DATASET || '';

let builder: ReturnType<typeof imageUrlBuilder> | null = null;

try {
    if (!projectId || !dataset) {
        throw new Error('Sanity projectId or dataset is missing. Image URLs may not work correctly.');
    }

    builder = imageUrlBuilder({
        projectId,
        dataset,
    });
} catch (error) {
    console.error(error instanceof Error ? error.message : error);
}

/**
 * Normalize image source so @sanity/image-url can read crop/hotspot.
 * We use asset.ref (camelCase) everywhere; Sanity's library expects asset._ref.
 * Therefore we add _ref when passing to Sanity.
 */
const normalizeImageSource = (source: SanityImageSource): SanityImageSource => {
    if (typeof source !== 'object' || source === null) {
        return source;
    }
    const { asset } = source as { asset?: { ref?: string; _type?: string; type?: string } };
    const hasValidAsset = Boolean(asset && typeof asset === 'object');
    const ref =
        hasValidAsset && typeof asset!.ref === 'string' && asset!.ref.trim() !== '' ? asset!.ref.trim() : undefined;

    if (!hasValidAsset || !ref) {
        return source;
    }
    return {
        ...source,
        asset: {
            _ref: ref,
            _type: asset!._type ?? asset!.type ?? 'reference',
        },
    } as SanityImageSource;
};

/** Default quality for all Sanity images (1–100). Balances visual quality and file size. */
const DEFAULT_QUALITY = 90;

/**
 * Helper function to generate Sanity image URLs with support for crop and hotspot.
 * Applies shared defaults for all images: auto=format, quality=90, fit=max (never upscale).
 * @param source - Sanity image source (can be image reference, asset object, etc.)
 * @returns Image URL builder instance with methods like .width(), .height(), .url(), etc., or null if source is missing
 */
export const urlFor = (source?: SanityImageSource | null) => {
    if (!source || !builder) {
        return null;
    }
    return builder.image(normalizeImageSource(source)).auto('format').quality(DEFAULT_QUALITY).fit('max');
};

/**
 * Build Open Graph image URL (1200×630, JPG, crop + hotspot).
 * Use for og:image meta. Crop and hotspot from the image object are applied when present.
 * Quality 85 aims for <300KB (best practice).
 * @param source - Sanity image source (e.g. seo.openGraph.image)
 * @returns Full CDN URL string or null if source/builder is missing
 */
export const buildOgImageUrl = (source?: SanityImageSource | null): string | null => {
    if (!source || !builder) {
        return null;
    }
    try {
        return builder
            .image(normalizeImageSource(source))
            .width(1200)
            .height(630)
            .fit('crop')
            .format('jpg')
            .quality(85)
            .url();
    } catch {
        return null;
    }
};
